/* ============================================================================
   bulk-reel.js — the 3D curved product reel
   ----------------------------------------------------------------------------
   WHAT IT IS
   Products laid out along a curve in 3D. Drag or flick horizontally and it
   spins like a slot-machine reel, with momentum, then settles onto the nearest
   product. Whatever sits in the middle is the hero: bigger, brighter, with its
   name and price. It never ends — run past the last product of one collection
   and the next collection continues seamlessly.

   ─── THE IMPORTANT PART: IT ONLY EVER BUILDS ~13 ELEMENTS ──────────────────
   A collection here can hold hundreds of products. Building a card for each one
   would put hundreds of images in the page — which is exactly the mistake that
   left this store's homepage with 25,000 DOM elements and a 14-second blocking
   time in July.

   So it doesn't. It builds a fixed ring of SLOTS — about 13, only what can be
   seen on the curve at once — and as the reel turns, each slot is re-pointed at
   a different product. Slot 4 might show product 2, then product 15, then
   product 240. The page cost is identical whether the collection has 20
   products or 2,000.

   This is called virtualisation, and it is the only reason a reel like this is
   safe to ship on a phone.

   ─── OTHER RULES BEING FOLLOWED ────────────────────────────────────────────
   • Movement is `transform` only — GPU work, never triggers layout.
   • Nothing animates while the section is off-screen (IntersectionObserver).
   • The animation loop stops completely once the reel is at rest. It is not a
     permanent requestAnimationFrame burning battery.
   • Honours prefers-reduced-motion: no spin physics, just plain tap-to-move.
   • Keyboard accessible: arrow keys move it, the centre item is a real link.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (!root || root.__reelInit) return;
    root.__reelInit = true;

    var ring = root.querySelector('[data-reel-ring]');

    /* IMPORTANT: `root` is the reel STAGE. The caption — name, price, badge and
       the add button — lives outside it as a sibling, so it must be queried
       from the surrounding section, not from the stage. Querying it from `root`
       silently returned null and the caption never filled in. */
    var scope = root.closest('.bulkr') || root.parentNode;
    var nameEl = scope.querySelector('[data-reel-name]');
    var priceEl = scope.querySelector('[data-reel-price]');
    var linkEl = scope.querySelector('[data-reel-link]');
    var tagEl = scope.querySelector('[data-reel-tag]');
    if (!ring) return;

    var items = [];
    try { items = JSON.parse(root.querySelector('[data-reel-data]').textContent || '[]'); } catch (e) {}
    if (!items.length) return;

    var SLOTS  = Math.min(13, Math.max(7, items.length));  // elements we actually build
    var STEP   = parseFloat(root.dataset.step || '18');    // degrees between products on the curve
    var RADIUS = parseFloat(root.dataset.radius || '520'); // how deep the curve bends

    /* ---- build the fixed slot ring, once ---- */
    var slots = [];
    for (var i = 0; i < SLOTS; i++) {
      var s = document.createElement('div');
      s.className = 'bulkr__slot';
      var img = document.createElement('img');
      img.alt = '';
      /* Transparent placeholder so a slot never exists with an empty src —
         an <img> with no source counts as a broken image and logs console
         noise, even for the split second before it is first bound. */
      img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
      img.loading = i < 5 ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.width = 300; img.height = 300;
      s.appendChild(img);
      ring.appendChild(s);
      slots.push({ el: s, img: img, bound: -1 });
    }

    /* CRITICAL: push the whole ring back by exactly the curve radius.
       Each slot is placed with translateZ(+RADIUS), which puts the centre card
       RADIUS pixels nearer the viewer than the stage — and anything nearer the
       viewer is magnified by perspective/(perspective - z). With a 660px radius
       and a 1000px perspective that magnified the centre card by ~2.9x: it
       filled the screen, crowded its neighbours and spilled over the heading.
       Offsetting the ring back by RADIUS puts the centre card at z=0, so it
       renders at its true CSS size and the others fall away behind it. */
    ring.style.transform = 'translateZ(' + (-RADIUS) + 'px)';

    var half = Math.floor(SLOTS / 2);
    var pos = 0;             // fractional index into `items`
    var vel = 0;             // items per frame
    var dragging = false;
    var raf = null;
    var visible = true;
    var lastCentre = -1;

    function wrap(i) { return ((i % items.length) + items.length) % items.length; }

    function bind(slot, index) {
      if (slot.bound === index) return;
      slot.bound = index;
      var it = items[wrap(index)];
      if (slot.img.getAttribute('src') !== it.img) slot.img.src = it.img;
      slot.img.alt = it.title;
      slot.el.dataset.url = it.url;
    }

    /* Centre product drives the caption — name and price only, as asked.
       Each time a new product reaches the middle we fire a tiny haptic tick on
       phones that support it. That is what gives a spinning reel its physical,
       slot-machine feel rather than feeling like a silent slideshow. */
    function paintCentre() {
      var c = wrap(Math.round(pos));
      if (c === lastCentre) return;
      var first = lastCentre === -1;
      lastCentre = c;
      var it = items[c];
      if (nameEl) nameEl.textContent = it.title;
      if (priceEl) priceEl.textContent = it.price;
      if (tagEl) tagEl.textContent = it.tag || '';
      if (!first && !reduceMotion && navigator.vibrate) {
        try { navigator.vibrate(8); } catch (e) {}
      }
      root.dispatchEvent(new CustomEvent('reel:change', { bubbles: true, detail: it }));
    }

    function render() {
      var base = Math.round(pos);
      for (var k = 0; k < SLOTS; k++) {
        var index = base + k - half;
        var offset = index - pos;                 // -half .. +half, fractional
        bind(slots[k], index);
        var ang = offset * STEP;
        var dist = Math.abs(offset);
        /* Five products should read clearly at once — the centre plus two
           either side. So the falloff is gentle up to dist 2, then drops away
           quickly so the edges of the curve don't turn into visual noise.
           An earlier version faded at 0.16 per step and effectively only the
           centre card was legible. */
        var scale = Math.max(0.62, 1 - dist * 0.07);
        var op = dist <= 2.2 ? 1 - dist * 0.06
               : Math.max(0, 0.87 - (dist - 2.2) * 0.55);
        slots[k].el.style.transform =
          'rotateY(' + ang.toFixed(2) + 'deg) translateZ(' + RADIUS + 'px) scale(' + scale.toFixed(3) + ')';
        slots[k].el.style.opacity = op.toFixed(3);
        slots[k].el.style.zIndex = String(100 - Math.round(dist * 10));
        slots[k].el.classList.toggle('is-centre', dist < 0.5);
      }
      paintCentre();
    }

    /* ---- momentum + snap: the "casino reel" feel ----
       Each frame we apply friction, then once it is crawling we ease onto the
       nearest whole product rather than stopping between two. */
    function tick() {
      raf = null;
      if (!visible) return;

      if (!dragging) {
        if (Math.abs(vel) > 0.0006) {
          pos += vel;
          vel *= 0.94;                              // friction
        } else {
          var target = Math.round(pos);
          var diff = target - pos;
          if (Math.abs(diff) > 0.001) {
            pos += diff * 0.18;                     // ease into the detent
          } else {
            pos = target; vel = 0; render();
            return;                                 // fully at rest — stop the loop
          }
        }
      }
      render();
      raf = requestAnimationFrame(tick);
    }
    function kick() { if (raf == null && visible) raf = requestAnimationFrame(tick); }

    /* ---- pointer drag ---- */
    var lastX = 0, lastT = 0;
    var PX_PER_ITEM = 92;   // how far a finger travels to advance one product

    root.addEventListener('pointerdown', function (e) {
      if (e.target.closest('[data-reel-link]')) return;
      dragging = true; vel = 0;
      lastX = e.clientX; lastT = e.timeStamp;
      root.classList.add('is-dragging');
      root.setPointerCapture && root.setPointerCapture(e.pointerId);
      kick();
    });

    root.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX;
      var dt = Math.max(1, e.timeStamp - lastT);
      pos -= dx / PX_PER_ITEM;
      vel = -(dx / PX_PER_ITEM) / dt * 16;          // convert to per-frame
      lastX = e.clientX; lastT = e.timeStamp;
      render();
    });

    function release() {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
      if (reduceMotion) { vel = 0; }
      else { vel = Math.max(-1.4, Math.min(1.4, vel)); }   // cap the spin speed
      kick();
    }
    root.addEventListener('pointerup', release);
    root.addEventListener('pointercancel', release);
    root.addEventListener('pointerleave', release);

    /* Tapping a product that isn't centred brings it to the middle rather than
       navigating — you can't sensibly tap a card that's turned away from you. */
    ring.addEventListener('click', function (e) {
      var slot = e.target.closest('.bulkr__slot');
      if (!slot || slot.classList.contains('is-centre')) return;
      e.preventDefault();
      var k = slots.findIndex(function (s) { return s.el === slot; });
      if (k < 0) return;
      vel = 0; pos = Math.round(pos) + (k - half); kick();
    });

    /* ---- desktop: spin with the scroll wheel / trackpad ----
       This is what makes the reel feel good with a mouse rather than only with
       a finger. A trackpad flick spins it hard, exactly like a swipe.

       Note the listener is bound to the REEL, not the document. A document-wide
       non-passive wheel listener forces the browser to wait on JavaScript before
       every scroll on every page — this theme had that bug in July and it hurt.
       Scoped to one element it costs nothing. We also only swallow the gesture
       when it is clearly horizontal or clearly deliberate, so the page can still
       be scrolled past the reel with a normal vertical wheel. */
    root.addEventListener('wheel', function (e) {
      var horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (!horizontal && Math.abs(e.deltaY) < 12) return;   // gentle vertical → let the page scroll
      var delta = horizontal ? e.deltaX : e.deltaY;
      e.preventDefault();
      vel += delta / 900;
      vel = Math.max(-1.4, Math.min(1.4, vel));
      kick();
    }, { passive: false });

    /* ---- keyboard ---- */
    root.setAttribute('tabindex', '0');
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { vel = 0; pos = Math.round(pos) + 1; kick(); e.preventDefault(); }
      if (e.key === 'ArrowLeft')  { vel = 0; pos = Math.round(pos) - 1; kick(); e.preventDefault(); }
    });

    /* "Add to my enquiry" — tick this product's category in the form and stay
       on the page. See the note in the section file for why this replaced a
       link to the product page. */
    var addBtn = scope.querySelector('[data-reel-add]');
    var addedEl = scope.querySelector('[data-reel-added]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var it = items[wrap(Math.round(pos))];
        if (!it) return;
        document.dispatchEvent(new CustomEvent('bulk:interest', { bubbles: true, detail: { category: it.cat || '', title: it.title } }));
        document.dispatchEvent(new CustomEvent('bulk:pick', { bubbles: true, detail: { title: it.title } }));
        if (addedEl) {
          addedEl.textContent = (it.cat ? it.cat : 'Added') + ' added to your enquiry ✓';
          clearTimeout(addBtn.__t);
          addBtn.__t = setTimeout(function () { addedEl.textContent = ''; }, 4000);
        }
        var form = document.getElementById('bulk-enquiry');
        if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try { if (window.dataLayer) window.dataLayer.push({ event: 'bulk_reel_add', category: it.cat }); } catch (e) {}
      });
    }

    /* arrows */
    root.querySelectorAll('[data-reel-nav]').forEach(function (b) {
      b.addEventListener('click', function () {
        vel = 0; pos = Math.round(pos) + (b.dataset.reelNav === 'next' ? 1 : -1); kick();
      });
    });

    /* ---- never run off-screen ---- */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          visible = en.isIntersecting;
          if (visible) kick(); else if (raf) { cancelAnimationFrame(raf); raf = null; }
        });
      }, { rootMargin: '150px' }).observe(root);
    }

    /* Start on the best seller — item 0 — sitting in the middle. */
    pos = 0;
    render();
  }

  function boot() { document.querySelectorAll('[data-bulk-reel]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
